import { useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { OverflowMenu } from "../components/common/OverflowMenu";
import { DeleteProjectModal } from "../components/projects/DeleteProjectModal";
import { ProjectFilesTab } from "../components/projects/ProjectFilesTab";
import { ProjectStudiesTab } from "../components/projects/ProjectStudiesTab";
import { AppShell } from "../components/shell/Shell";
import { useLayers, useProject, useRuns } from "../hooks/useProjects";

const TABS = ["files", "studies"] as const;
type Tab = (typeof TABS)[number];

function parseTab(value: string | null): Tab {
  return (TABS as readonly string[]).includes(value ?? "")
    ? (value as Tab)
    : "files";
}

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError, error } = useProject(id);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseTab(searchParams.get("tab"));
  const setActiveTab = (tab: Tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === "files") next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next);
  };
  const [showDelete, setShowDelete] = useState(false);
  const navigate = useNavigate();

  // Pre-fetch the counts we surface in the delete modal so it doesn't
  // pop up empty. These hooks short-circuit when ``id`` is undefined.
  const { data: layers } = useLayers(id);
  // We only have one "default" study currently; counts of runs across
  // all studies would require fanning out — the layers/studies counts
  // are enough for the warning copy.
  const firstStudyId = data?.studies[0]?.id;
  const { data: runs } = useRuns(firstStudyId);

  if (isLoading) {
    return (
      <AppShell width="workspace">
        <div role="status">Loading project…</div>
      </AppShell>
    );
  }
  if (isError || !data) {
    return (
      <AppShell width="workspace">
        <div role="alert">
          Failed to load project:{" "}
          {error instanceof Error ? error.message : "not found"}
          <div>
            <Link to="/projects">Back to projects</Link>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell width="workspace">
      <Link to="/projects" data-testid="back-to-projects">
        ← Back to projects
      </Link>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        <h1 data-testid="project-name" style={{ margin: "0.5rem 0 0" }}>
          {data.name}
        </h1>
        <OverflowMenu
          triggerTestId="project-overflow-trigger"
          ariaLabel={`More actions for ${data.name}`}
          items={[
            {
              label: "Delete project",
              destructive: true,
              onSelect: () => setShowDelete(true),
              "data-testid": "project-overflow-delete",
            },
          ]}
        />
      </div>
      <div style={{ color: "#666", marginBottom: "1rem" }}>
        Status: <strong>{data.status}</strong>
      </div>

      <nav
        role="tablist"
        aria-label="Project sections"
        style={{
          borderBottom: "1px solid #e5e7eb",
          marginBottom: "1rem",
          display: "flex",
          gap: "1rem",
        }}
      >
        <button
          role="tab"
          aria-selected={activeTab === "files"}
          data-testid="tab-files"
          onClick={() => setActiveTab("files")}
          style={tabStyle(activeTab === "files")}
        >
          Files
        </button>
        <button
          role="tab"
          aria-selected={activeTab === "studies"}
          data-testid="tab-studies"
          onClick={() => setActiveTab("studies")}
          style={tabStyle(activeTab === "studies")}
        >
          Studies
        </button>
      </nav>

      {activeTab === "files" && <ProjectFilesTab projectId={data.id} />}
      {activeTab === "studies" && (
        <ProjectStudiesTab projectId={data.id} studies={data.studies} />
      )}

      {showDelete && (
        <DeleteProjectModal
          projectId={data.id}
          projectName={data.name}
          fileCount={layers?.length}
          studyCount={data.studies.length}
          runCount={runs?.length}
          hasBoundary={data.boundary_layer_id !== null}
          onClose={() => setShowDelete(false)}
          onDeleted={() => navigate("/projects", { replace: true })}
        />
      )}
    </AppShell>
  );
}

function tabStyle(active: boolean): React.CSSProperties {
  return {
    padding: "0.5rem 1rem",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontWeight: active ? 600 : 400,
    borderBottom: active ? "2px solid #2563eb" : "2px solid transparent",
  };
}
